import type { CardProps } from '@nextui-org/react';
import { Card, Chip, Image } from '@nextui-org/react';
import type { ReactNode } from 'react';
import React, { useState } from 'react';
import { match } from 'ts-pattern';

import {
  CrownCardTrendingIcon,
  DownCardTrendingIcon,
  DownloadCardTrendingIcon,
  HeartCardTrendingActiveIcon,
  HeartCardTrendingIcon,
  PauseIcon,
  PlayIcon,
} from '@/assets';
import { Images } from '@/components';

interface CardTrendingProps {
  chip: string;
  img: string;
  downloads: number;
  typeFooter?: 'home-page' | 'airight-audio' | 'other';
  imgAvatar: string;
  className: string;
  hiddenIconDownloads?: boolean;
  hiddenIconCrown?: boolean;
  footer?: ReactNode;
}

const CardTrending: React.FC<CardTrendingProps & CardProps> = ({
  img,
  typeFooter = 'other',
  imgAvatar,
  downloads,
  className,
  hiddenIconCrown = false,
  hiddenIconDownloads = false,
  footer,
  chip,
  ...props
}) => {
  const [isLike, setIsLike] = useState(false);

  const RenderFooter = () => {
    return match(typeFooter)
      .with('airight-audio', () => (
        <div className="absolute bottom-0 z-10 flex w-full flex-col items-start bg-transparent p-7">
          <span className="text-16 font-semibold text-white">
            Eternity
            <div className="flex items-center">
              <PauseIcon />
              <PlayIcon className="ml-2" />
            </div>
          </span>
        </div>
      ))
      .with('home-page', () => (
        <div className="absolute bottom-0 z-10 flex w-full items-end justify-between bg-transparent p-7 opacity-0 duration-400 group-hover:opacity-100">
          <Image alt="card image" className="h-9 w-9 rounded-[8px] object-cover" src={imgAvatar} />
          <div className="flex items-center">
            <DownCardTrendingIcon className="mr-1" />
            <span className="text-14 font-normal text-white">{downloads}</span>
          </div>
        </div>
      ))
      .otherwise(() => (
        <div className="absolute bottom-0 z-10 flex w-full items-end justify-between bg-transparent p-7 opacity-0 duration-400 group-hover:opacity-100">
          {footer ?? ''}
        </div>
      ));
  };

  return (
    <Card
      isFooterBlurred
      radius="lg"
      className={`group relative h-full cursor-pointer border-none ${className ?? ''}`}
      {...props}
    >
      <div className="absolute top-0 z-20 flex w-full items-center bg-transparent p-7 text-center group-hover:hidden">
        <Chip className="ml-2 rounded-[100px] bg-black/50 text-white">{chip}</Chip>
      </div>
      <div className="absolute top-0 z-20 flex w-full items-start justify-between bg-transparent p-7 opacity-0 duration-400 group-hover:opacity-100">
        <div className="flex items-center">
          {!hiddenIconCrown && <CrownCardTrendingIcon />}
          <Chip className="ml-2 rounded-[100px] bg-black/50 text-white">{chip}</Chip>
        </div>
        <div className="flex flex-col">
          {isLike ? (
            <HeartCardTrendingActiveIcon
              className="cursor-pointer"
              onClick={() => setIsLike((prev) => !prev)}
            />
          ) : (
            <HeartCardTrendingIcon
              className="cursor-pointer"
              onClick={() => setIsLike((prev) => !prev)}
            />
          )}
          {!hiddenIconDownloads && <DownloadCardTrendingIcon className="mt-3 cursor-pointer" />}
        </div>
      </div>
      <Images
        className="object-cover duration-400 group-hover:scale-125"
        alt="card image"
        src={img}
      />
      {RenderFooter()}
    </Card>
  );
};

export default CardTrending;
